import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { BodyTextMedium, HeadingText50, pxToRem } from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { ExternalLink } from 'src/shared/components/ExternalLink';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { config } from 'src/config/config';
import { navigationService, toastNotificationsService } from 'src/config/rootService';
import { color, font } from 'src/shared/styles/variables';

import { useVideoSessionPageQuery } from '../documents/videoSessionPageQuery.generated';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { usePathwayByAssignmentQuery } from 'src/modules/pathways/documents/pathwayByAssignment.generated';

const StyledPageWrapper = styled.div({
    background: 'linear-gradient(180deg, #2E3B6C 41.88%, #0A1745 100%)',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    color: color.white,
    justifyContent: 'center',
    alignItems: 'center',
});

const StyledLoadingMessage = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: 3,
    alignItems: 'center',
    justifyContent: 'center',
});
const StyledHeader = styled(HeadingText50)({ color: colors.white, marginBottom: pxToRem(4) });
const StyledVideoLinkWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
});

const fadeIn = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
     }
`;

const VideoText = styled(BodyTextMedium)({ animation: `${fadeIn} 0.1s ease` });
const VideoLink = styled(ExternalLink)(
    {
        animation: `${fadeIn} 0.1s ease`,
        userSelect: 'auto',
    },
    font.medium(font.size.medium),
);

const TIME_UNTIL_ZOOM_REDIRECT = 2000; // 2 secs
const TIME_UNTIL_RATINGS_REDIRECT = 500; // 0.5 secs

interface Props {
    sessionUuid: string;
}
export const JoinVideoSessionPage: React.FC<Props> = ({ sessionUuid }) => {
    const { t } = useTranslation();
    const {
        data: videoSessionData,
        loading: videoSessionLoading,
        called: videoSessionCalled,
        error: videoSessionError,
    } = useVideoSessionPageQuery({
        variables: { uuid: sessionUuid },
    });

    const {
        data: pathwayAssignmentData,
        loading: pathwayAssignmentLoading,
        called: pathwayAssignmentCalled,
        error: pathwayAssignmentError,
    } = usePathwayByAssignmentQuery({
        variables: {
            providerAssignmentUuid: videoSessionData?.coachingVisit?.coachAssignment?.uuid,
        },
        skip: !videoSessionData?.coachingVisit?.coachAssignment?.uuid,
    });

    const [openedZoom, setOpenedZoom] = useState(false);

    // Automatically open Zoom window.
    useEffect(() => {
        let zoomDelayedRedirect: number;
        if (videoSessionData?.coachingVisit?.zoomUrl) {
            zoomDelayedRedirect = window.setTimeout(() => {
                if (videoSessionData?.coachingVisit?.zoomUrl && !openedZoom) {
                    const newWindow = window.open(videoSessionData.coachingVisit.zoomUrl);
                    setOpenedZoom(!!newWindow);
                }
            }, TIME_UNTIL_ZOOM_REDIRECT);
        }
        return () => clearTimeout(zoomDelayedRedirect);
    }, [sessionUuid, videoSessionData]);

    // Redirect to Ratings after Zoom has been open for a little bit.
    useEffect(() => {
        let ratingsRedirect: number;
        if (openedZoom) {
            ratingsRedirect = window.setTimeout(() => {
                if (
                    videoSessionData?.coachingVisit?.coachAssignment &&
                    videoSessionData?.coachingVisit?.creationSource === 'PATHWAYS'
                ) {
                    if (pathwayAssignmentData && pathwayAssignmentData.pathwayByAssignment) {
                        navigationService.navigate(
                            `/pathways/${
                                pathwayAssignmentData.pathwayByAssignment.uuid
                            }/session-complete?activeSession=${
                                (pathwayAssignmentData.pathwayByAssignment.memberPathway
                                    ?.currentSessionIndex ?? 0) + 1
                            }`,
                        );
                    } else {
                        navigationService.navigate('/pathways');
                    }
                } else {
                    navigationService.navigate('/rate-session', { session: sessionUuid });
                }
            }, TIME_UNTIL_RATINGS_REDIRECT);
        }
        return () => clearTimeout(ratingsRedirect);
    }, [openedZoom]);

    const handleError = (message: string = config.messages.genericError) => {
        toastNotificationsService.renderErrorToast(message);
        navigationService.navigate('/');

        return null;
    };

    const noDataReceived =
        videoSessionCalled &&
        !videoSessionLoading &&
        !videoSessionData &&
        !pathwayAssignmentLoading &&
        pathwayAssignmentCalled;

    if (videoSessionError || noDataReceived || pathwayAssignmentError) {
        return handleError(
            (videoSessionError && videoSessionError.message) ||
                (pathwayAssignmentError && pathwayAssignmentError.message),
        );
    }

    return (
        <StyledPageWrapper>
            <StyledLoadingMessage>
                <div>
                    {openedZoom ? null : (
                        <CenteredSpinner
                            color={colors.white}
                            size={40}
                            css={{
                                margin: '6rem',
                                position: 'relative',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                transform: 'unset',
                                top: 'unset',
                                left: 'unset',
                            }}
                        />
                    )}
                </div>
                <StyledHeader>{t('videoSessions:join_video.title')}</StyledHeader>
                <BodyTextMedium>{t('videoSessions:join_video.description')}</BodyTextMedium>
            </StyledLoadingMessage>
            <StyledVideoLinkWrapper>
                {videoSessionCalled &&
                !videoSessionLoading &&
                videoSessionData?.coachingVisit?.zoomUrl &&
                pathwayAssignmentCalled &&
                !pathwayAssignmentLoading ? (
                    <React.Fragment>
                        <VideoText>{t('videoSessions:join_video.not_load')}</VideoText>
                        <VideoText>
                            <VideoLink
                                href={videoSessionData.coachingVisit.zoomUrl}
                                inheritStyling
                                onClick={() => setOpenedZoom(true)}
                            >
                                {videoSessionData.coachingVisit.zoomUrl}
                            </VideoLink>
                        </VideoText>
                    </React.Fragment>
                ) : null}
            </StyledVideoLinkWrapper>
        </StyledPageWrapper>
    );
};
